import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _get from "lodash/get";
import _values from "lodash/values";
var _excluded = ["type"];
import PropTypes from 'prop-types';
import React from 'react';
import ApplyNow, { ApplyNowLink, FinishType, LinkType, Monetizable } from '@nerdwallet/react-apply-now';
import { LINK_TYPES, TRK_PRODUCT_LOCATION } from './constants';
var ApplyNowWrapper = function ApplyNowWrapper(_ref) {
  var type = _ref.type,
    props = _objectWithoutProperties(_ref, _excluded);
  var updatedProps = props;
  var Component = type === LINK_TYPES.BUTTON ? ApplyNow : ApplyNowLink;

  // Ensure CTA component receives correct prop for text.
  //
  // This is necessary because <ApplyNow /> expects a `text` prop,
  // however <ApplyNowLink /> expects a `children` prop.
  if (type !== LINK_TYPES.BUTTON && updatedProps.children == null) {
    updatedProps.children = updatedProps.text;
    delete updatedProps.text;
  }
  if (type === LINK_TYPES.LINK && props.trkTaxonomy) {
    var _props$trkTaxonomy;
    updatedProps.vertical = updatedProps.vertical || (props === null || props === void 0 ? void 0 : (_props$trkTaxonomy = props.trkTaxonomy) === null || _props$trkTaxonomy === void 0 ? void 0 : _props$trkTaxonomy.vertical) || '';
  }
  return /*#__PURE__*/React.createElement(Component, updatedProps);
};

/**
 * Prop types inherited from the ApplyNow and ApplyNowLink components.
 * ApplyNow: https://github.com/NerdWallet/react-components/blob/master/components/apply-now/src/apply-now.jsx#L131
 * ApplyNowLink: https://github.com/NerdWallet/react-components/blob/master/components/apply-now/src/apply-now-link.jsx#L173
 *
 * Note: certain props only apply when LINK_TYPES.BUTTON is used.
 * These are indicated below.
 */
var inheritedPropTypes = {
  /** BetaProductShoppingFinish:additional_attributes
   https://github.com/NerdWallet/schemas/blob/master/protobuf/src/event/BetaProductShoppingFinish.proto#L67 */
  additionalAttributes: PropTypes.object,
  /** Text to display under the button. Defaults to "On issuer's website" (button only). */
  annotationText: PropTypes.string,
  /** Children to render (link only). */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  /** Classes to apply to the link. */
  className: PropTypes.string,
  /** If you pass in an array of children or text, it will be wrapped with this node type. */
  component: PropTypes.any,
  /** Disable annotation text (button only). */
  disableAnnotation: PropTypes.bool,
  /** Disable the link entirely (button only). */
  disabled: PropTypes.bool,
  /** BetaProductShoppingFinish:finish_type
   https://github.com/NerdWallet/schemas/blob/master/protobuf/src/event/BetaProductShoppingFinish.proto#L15 */
  finishType: PropTypes.oneOf(_values(FinishType)),
  /** BetaProductShoppingFinish:has_prequalified
   https://github.com/NerdWallet/schemas/blob/master/protobuf/src/event/BetaProductShoppingFinish.proto#L80 */
  hasPrequalified: PropTypes.bool,
  /** Icon element to use (button only). */
  icon: PropTypes.node,
  /** Unique ID used for impression tracking. */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** BetaProductShoppingFinish.impression_id
   https://github.com/NerdWallet/schemas/blob/master/protobuf/src/event/BetaProductShoppingFinish.proto#L76 */
  impressionId: PropTypes.string,
  /** Indicates if a cc-specific impression event should be used. */
  isCC: PropTypes.bool,
  /** Issuer name to use in the annotation text if `issuerNameApply` is true (button only). */
  issuer: PropTypes.string,
  /** Include the issuer name in the apply link (button only). */
  issuerNameApply: PropTypes.bool,
  /** The apply now link. */
  link: PropTypes.string.isRequired,
  /** BetaProductShoppingFinish.link_type (link only)
   https://github.com/NerdWallet/schemas/blob/master/protobuf/src/event/BetaProductShoppingFinish.proto#L93 */
  linkType: PropTypes.oneOf(_values(LinkType)),
  /** Log an impression. */
  logImpression: PropTypes.bool,
  /** BetaProductShoppingFinish.monetizable
   https://github.com/NerdWallet/schemas/blob/master/protobuf/src/event/BetaProductShoppingFinish.proto#L79 */
  monetizable: PropTypes.oneOf(_values(Monetizable)),
  /** Optional click handler. */
  onClick: PropTypes.func,
  /** Tracking page number. */
  pageNumber: PropTypes.number,
  /** Phone number that can be used to apply (button only). */
  pay_per_call: PropTypes.string,
  /** Primary vs secondary button (button only). */
  primary: PropTypes.bool,
  /** Why this card displayed to the user. */
  productDisplayDriver: PropTypes.string,
  /** The ID of a financial product */
  productID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Tracking product location. */
  productLocation: PropTypes.string,
  /** Tracking product position. */
  productPosition: PropTypes.number,
  /** Specifies the referring url on the link */
  referringUrl: PropTypes.string,
  /** Link rel attribute. */
  rel: PropTypes.string,
  /** Section name in a section interface */
  sectionName: PropTypes.string,
  /** Tracking section position. */
  sectionPosition: PropTypes.number,
  /** Button text (button only). */
  text: PropTypes.string,
  /** The page taxonomy to use for tracking. */
  trkTaxonomy: PropTypes.object
};
ApplyNowWrapper.propTypes = _objectSpread({
  /** Link type (button vs. link). Required. */
  type: PropTypes.oneOf(_values(LINK_TYPES)).isRequired
}, inheritedPropTypes);
ApplyNowWrapper.defaultProps = {
  productDisplayDriver: 'user_selected',
  // eslint-disable-line react/default-props-match-prop-types
  finishType: FinishType.EXTERNAL_APPLICATION,
  // eslint-disable-line react/default-props-match-prop-types
  productLocation: TRK_PRODUCT_LOCATION,
  // eslint-disable-line react/default-props-match-prop-types
  trkTaxonomy: _get(global, 'window.nwPageTaxonomy') // eslint-disable-line react/default-props-match-prop-types
};

export default ApplyNowWrapper;